import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Button from '../../Button';
import './HeroBanner.css';

/**
 * HeroBanner Component
 *
 * Full-width hero banner with text content on one side
 * and a visual (image) on the other.
 *
 * Figma node: 3869:14163
 *
 * Desktop (≥ 1024px): side-by-side layout, 512px height,
 *   content left + visual right (aligned to bottom).
 * Mobile: stacked, content on top, visual below.
 *
 * Uses DS tokens: colors, spacing, typography, radius, transitions.
 * Reuses: Button component (primary/alternative by default).
 *
 * Props control visibility of each section and content overrides.
 */

const HeroBanner = ({
  // Visibility toggles (match Figma component properties)
  showSubtitle = true,
  showTitle = true,
  showCopy = true,
  showCta = true,
  showCaption = true,

  // Content
  subtitle = '',
  title = '',
  copy = '',
  caption = '',

  // CTA
  ctaLabel = '',
  ctaVariant = 'alternative',
  ctaIcon = <ArrowRight />,
  ctaOnClick,
  ctaHref,

  // Visual
  image = null,
  imageAlt = '',

  // Content slot — when provided, replaces the default subtitle/copy/cta/caption
  // rows on the left side (title is preserved if showTitle && title).
  // Use for custom content such as forms, widgets, etc.
  contentSlot = null,

  // Overlay (e.g. open-shop card on top of visual)
  overlay = null,

  // Style
  backgroundColor = null,
  backgroundImage = null,
  theme = 'light',
  className = '',
}) => {
  const rootStyle = {};
  if (backgroundColor) rootStyle.backgroundColor = backgroundColor;
  if (backgroundImage) {
    rootStyle.backgroundImage = `url(${backgroundImage})`;
    rootStyle.backgroundSize = 'cover';
    rootStyle.backgroundPosition = 'center';
  }

  const ctaProps = {};
  if (ctaHref) ctaProps.as = 'a';
  if (ctaHref) ctaProps.href = ctaHref;
  if (ctaOnClick) ctaProps.onClick = ctaOnClick;

  const themeClass = theme === 'dark' ? ' hero-block--dark' : '';
  const contentSlotClass = contentSlot ? ' hero-block--has-content-slot' : '';

  return (
    <section
      className={`hero-block${themeClass}${contentSlotClass}${className ? ` ${className}` : ''}`}
      style={rootStyle}
    >
      <div className="hero-block__container">
        {/* ── Content side ── */}
        <div className="hero-block__content">
          <div className="hero-block__wrapper">
            {showSubtitle && subtitle && !contentSlot && (
              <div className="hero-block__subtitle-row">
                <p className="hero-block__subtitle typo-h5">{subtitle}</p>
              </div>
            )}

            {showTitle && title && (
              <div className="hero-block__title-row">
                <h1 className="hero-block__title typo-h1-editorial">{title}</h1>
              </div>
            )}

            {contentSlot ? (
              <div className="hero-block__content-slot">{contentSlot}</div>
            ) : (
              <>
                {showCopy && copy && (
                  <div className="hero-block__copy-row">
                    <p className="hero-block__copy typo-body">{copy}</p>
                  </div>
                )}

                {showCta && ctaLabel && (
                  <div className="hero-block__cta-row">
                    <Button
                      size="primary"
                      variant={ctaVariant}
                      iconRight={ctaIcon}
                      {...ctaProps}
                    >
                      {ctaLabel}
                    </Button>
                  </div>
                )}

                {showCaption && caption && (
                  <div className="hero-block__caption-row">
                    <p className="hero-block__caption typo-body">{caption}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Visual side ── */}
        <div className="hero-block__visual">
          {image && (
            <img
              src={image}
              alt={imageAlt}
              className="hero-block__visual-image"
            />
          )}
          {overlay && (
            <div className="hero-block__overlay">
              {overlay}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

HeroBanner.propTypes = {
  // Visibility
  showSubtitle: PropTypes.bool,
  showTitle: PropTypes.bool,
  showCopy: PropTypes.bool,
  showCta: PropTypes.bool,
  showCaption: PropTypes.bool,

  // Content
  subtitle: PropTypes.string,
  title: PropTypes.string,
  copy: PropTypes.string,
  caption: PropTypes.string,

  // CTA
  ctaLabel: PropTypes.string,
  ctaVariant: PropTypes.oneOf([
    'main', 'alternative', 'danger', 'outline', 'black', 'white', 'gray', 'text',
  ]),
  ctaIcon: PropTypes.node,
  ctaOnClick: PropTypes.func,
  ctaHref: PropTypes.string,

  // Visual
  image: PropTypes.string,
  imageAlt: PropTypes.string,

  // Overlay
  overlay: PropTypes.node,

  // Content slot (custom left-side content — replaces copy/cta/caption)
  contentSlot: PropTypes.node,

  // Style
  backgroundColor: PropTypes.string,
  backgroundImage: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  className: PropTypes.string,
};

export default HeroBanner;
